import React from 'react';
import PropTypes from 'prop-types';
import { getLocalUser } from 'utils/helpers';
import { isSuperUser } from 'utils/workflowAccess';

export const getUserPrivileges = () => {
  const user = JSON.parse(getLocalUser() || '{}');
  return user.adminRoles
    ?.map(
      (role) =>
        role?.roles?.privileges?.map((privilege) => privilege.name) || []
    )
    .flat()
    .flat();
};

export const hasAccess = (restrictedPermissions = []) => {
  const userPermissions = getUserPrivileges();
  return !!restrictedPermissions?.some((a) => userPermissions?.includes(a));
};

const CanView = (props) =>
  hasAccess(props.can) ? (
    <React.Fragment>{props.children}</React.Fragment>
  ) : null;

export const CanViewTabs = (can) => (hasAccess(can) ? false : true);

CanView.propTypes = {
  can: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.any,
};

export const CanFilter = (list) =>
  list.filter((item) => hasAccess(item.can || []));

export default CanView;

export const Permissions = {
  // App Report
  CREATE_REPORT: 'appreport:c',
  READ_REPORT: 'appreport:r',
  UPDATE_REPORT: 'appreport:u',
  DELETE_REPORT: 'appreport:d',

  // Applications
  CREATE_APPLICATIONS: 'application:c',
  READ_APPLICATIONS: 'application:r',
  UPDATE_APPLICATIONS: 'application:u',
  DELETE_APPLICATIONS: 'application:d',

  // Forms
  CREATE_FORMS: 'forms:c',
  READ_FORMS: 'forms:r',
  UPDATE_FORMS: 'forms:u',
  DELETE_FORMS: 'forms:d',

  // course
  CREATE_COURSES: 'course:c',
  READ_COURSES: 'course:r',
  UPDATE_COURSES: 'course:u',
  DELETE_COURSES: 'course:d',

  // users
  CREATE_USERS: 'users:c',
  READ_USERS: 'users:r',
  UPDATE_USERS: 'users:u',
  DELETE_USERS: 'users:d',

  // Manage roles
  CREATE_ROLES: 'roles:c',
  READ_ROLES: 'roles:r',
  UPDATE_ROLES: 'roles:u',
  DELETE_ROLES: 'roles:d',

  // template
  CREATE_TEMPLATE: 'templates:c',
  READ_TEMPLATE: 'templates:r',
  UPDATE_TEMPLATE: 'templates:u',
  DELETE_TEMPLATE: 'templates:d',

  // adminlevels
  CREATE_ADMINLEVEL: 'adminlevel:c',
  READ_ADMINLEVEL: 'adminlevel:r',
  UPDATE_ADMINLEVEL: 'adminlevel:u',
  DELETE_ADMINLEVEL: 'adminlevel:d',

  // adminlevel items
  CREATE_ADMINLEVEL_ITEM: 'levelitem:c',
  READ_ADMINLEVEL_ITEM: 'levelitem:r',
  UPDATE_ADMINLEVEL_ITEM: 'levelitem:u',
  DELETE_ADMINLEVEL_ITEM: 'levelitem:d',

  // application group
  CREATE_APPLICATION_GROUP: 'appgroups:c',
  READ_APPLICATION_GROUP: 'appgroups:r',
  UPDATE_APPLICATION_GROUP: 'appgroups:u',
  DELETE_APPLICATION_GROUP: 'appgroups:d',

  // application group item
  CREATE_APPLICATION_GROUP_ITEM: 'groupitem:c',
  READ_APPLICATION_GROUP_ITEM: 'groupitem:r',
  UPDATE_APPLICATION_GROUP_ITEM: 'groupitem:u',
  DELETE_APPLICATION_GROUP_ITEM: 'groupitem:d',

  // paylog configuration
  CREATE_PAYLOG: 'paylog:c',
  READ_PAYLOG: 'paylog:r',
  UPDATE_PAYLOG: 'paylog:u',
  DELETE_PAYLOG: 'paylog:d',

  // mamange candidate groups
  CREATE_CANDIDATES_GROUP: 'candigroup:c',
  READ_CANDIDATES_GROUP: 'candigroup:r',
  UPDATE_CANDIDATES_GROUP: 'candigroup:u',
  DELETE_CANDIDATES_GROUP: 'candigroup:d',

  // mamange candidates
  CREATE_CANDIDATES: 'candidate:c',
  READ_CANDIDATES: 'candidate:r',
  UPDATE_CANDIDATES: 'candidate:u',
  DELETE_CANDIDATES: 'candidate:d',

  // Manage Statistics
  CREATE_STATISTIC: 'statistics:c',
  READ_STATISTIC: 'statistics:r',
  UPDATE_STATISTIC: 'statistics:u',
  DELETE_STATISTIC: 'statistics:d',

  // Manage Organisations
  CREATE_ORGANISATION: 'organisation:c',
  READ_ORGANISATION: 'organisation:r',
  UPDATE_ORGANISATION: 'organisation:u',
  DELETE_ORGANISATION: 'organisation:d',

  // Manage email
  CREATE_EMAIL: 'candiemail:c',
  READ_EMAIL: 'candiemail:r',
  UPDATE_EMAIL: 'candiemail:u',
  DELETE_EMAIL: 'candiemail:d',

  // Manage Payments
  READ_PAYMENT: 'payments:r',
  UPDATE_PAYMENT: 'payments:u',

  // Manage Coupons
  READ_COUPONS: 'coupons:r',
  UPDATE_COUPONS: 'coupons:u',
  CREATE_COUPONS: 'coupons:c',
  DELETE_COUPONS: 'coupons:d',

  READ_REMINDER: 'reminder:r',
  UPDATE_REMINDER: 'reminder:u',
  CREATE_REMINDER: 'reminder:c',
  DELETE_REMINDER: 'reminder:d',

  // Manage Workflow
  CREATE_WORKFLOW: 'workflow:c',
  READ_WORKFLOW: 'workflow:r',
  UPDATE_WORKFLOW: 'workflow:u',
  DELETE_WORKFLOW: 'workflow:d',
};

export const canViewCoupons = [
  Permissions.READ_COUPONS,
  Permissions.UPDATE_COUPONS,
  Permissions.CREATE_COUPONS,
  Permissions.DELETE_COUPONS
];
export const canViewTransactions = [
  Permissions.READ_PAYMENT,
  Permissions.UPDATE_PAYMENT
];
export const canViewReminder = [
  Permissions.READ_REMINDER,
  Permissions.UPDATE_REMINDER,
  Permissions.CREATE_REMINDER,
  Permissions.DELETE_REMINDER,
];
export const canHandleEmail = [
  Permissions.CREATE_EMAIL,
  Permissions.READ_EMAIL,
  Permissions.UPDATE_EMAIL,
  Permissions.DELETE_EMAIL,
];
export const canHandleReport = [
  Permissions.CREATE_REPORT,
  Permissions.READ_REPORT,
  Permissions.UPDATE_REPORT,
  Permissions.DELETE_REPORT,
];
export const canViewRoles = [
  Permissions.CREATE_ROLES,
  Permissions.READ_ROLES,
  Permissions.UPDATE_ROLES,
  Permissions.DELETE_ROLES,
];
export const canViewApplications = [
  Permissions.CREATE_APPLICATIONS,
  Permissions.READ_APPLICATIONS,
  Permissions.UPDATE_APPLICATIONS,
  Permissions.DELETE_APPLICATIONS,
];
export const canViewCourses = [
  Permissions.CREATE_COURSES,
  Permissions.READ_COURSES,
  Permissions.UPDATE_COURSES,
  Permissions.DELETE_COURSES,
];
export const canViewForms = [
  Permissions.CREATE_FORMS,
  Permissions.READ_FORMS,
  Permissions.UPDATE_FORMS,
  Permissions.DELETE_FORMS,
];
export const canViewUsers = [
  Permissions.CREATE_USERS,
  Permissions.READ_USERS,
  Permissions.UPDATE_USERS,
  Permissions.DELETE_USERS,
];
export const canViewTemplates = [
  Permissions.CREATE_TEMPLATE,
  Permissions.READ_TEMPLATE,
  Permissions.UPDATE_TEMPLATE,
  Permissions.DELETE_TEMPLATE,
];
export const canViewAdminlevel = [
  Permissions.CREATE_ADMINLEVEL,
  Permissions.READ_ADMINLEVEL,
  Permissions.UPDATE_ADMINLEVEL,
  Permissions.DELETE_ADMINLEVEL,
];
export const canViewAdminlevelItem = [
  Permissions.CREATE_ADMINLEVEL_ITEM,
  Permissions.READ_ADMINLEVEL_ITEM,
  Permissions.UPDATE_ADMINLEVEL_ITEM,
  Permissions.DELETE_ADMINLEVEL_ITEM,
];
export const canViewApplicationGroup = [
  Permissions.CREATE_APPLICATION_GROUP,
  Permissions.READ_APPLICATION_GROUP,
  Permissions.UPDATE_APPLICATION_GROUP,
  Permissions.DELETE_APPLICATION_GROUP,
];
export const canViewApplicationGroupItem = [
  Permissions.CREATE_APPLICATION_GROUP_ITEM,
  Permissions.READ_APPLICATION_GROUP_ITEM,
  Permissions.UPDATE_APPLICATION_GROUP_ITEM,
  Permissions.DELETE_APPLICATION_GROUP_ITEM,
];
export const canViewPaylog = [
  Permissions.CREATE_PAYLOG,
  Permissions.READ_PAYLOG,
  Permissions.UPDATE_PAYLOG,
  Permissions.DELETE_PAYLOG,
];
export const canViewCandidates = [
  Permissions.CREATE_CANDIDATES,
  Permissions.READ_CANDIDATES,
  Permissions.UPDATE_CANDIDATES,
  Permissions.DELETE_CANDIDATES,
];
export const canViewCandidateGroups = [
  Permissions.CREATE_CANDIDATES_GROUP,
  Permissions.READ_CANDIDATES_GROUP,
  Permissions.UPDATE_CANDIDATES_GROUP,
  Permissions.DELETE_CANDIDATES_GROUP,
];
export const canViewStatistics = [
  Permissions.CREATE_STATISTIC,
  Permissions.READ_STATISTIC,
  Permissions.UPDATE_STATISTIC,
  Permissions.DELETE_STATISTIC,
];
export const canViewSystemSettings = [
  Permissions.CREATE_ORGANISATION,
  Permissions.READ_ORGANISATION,
  Permissions.UPDATE_ORGANISATION,
  Permissions.DELETE_ORGANISATION,
];

export const canManagePortalAlias = [Permissions.UPDATE_ORGANISATION];

export const canSeePortalAlias = () => {
  if (hasAccess(canManagePortalAlias)) {
    return true;
  }
  try {
    return isSuperUser(JSON.parse(getLocalUser() || '{}'));
  } catch (e) {
    return false;
  }
};

export const canViewWorkflow = [
  Permissions.CREATE_WORKFLOW,
  Permissions.READ_WORKFLOW,
  Permissions.UPDATE_WORKFLOW,
  Permissions.DELETE_WORKFLOW,
];

export const canViewRoutes = [
  ...canViewApplications,
  ...canViewForms,
  ...canViewUsers,
];
